import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { StoryLearningIndexItem } from '@/content/stories'
import {
  getGrammarFamiliarityStatuses,
  getWordFamiliarityStatuses
} from '@/lib/familiarity'
import { learningContentKey } from '@/lib/learning-state'
import { storyLearningLocator } from '@/lib/learning-state-core'
import {
  scoreStoryRecommendations,
  selectStoryRecommendationCandidates,
  STORY_RECOMMENDATION_CANDIDATE_LIMIT,
  STORY_RECOMMENDATION_LIMIT,
  type StoryProgressLike
} from '@/lib/story-recommendations-core'
import { useAuth } from './useAuth'
import { useLearningStates } from './useLearningState'

function stableKey(values: Array<number | string>) {
  return values.join(',')
}

function uniqueNumbers(values: number[]) {
  return Array.from(new Set(values)).sort((a, b) => a - b)
}

function uniqueStrings(values: string[]) {
  return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b))
}

export function useStoryRecommendations({
  stories,
  currentSlug = null,
  candidateLimit = STORY_RECOMMENDATION_CANDIDATE_LIMIT,
  limit = STORY_RECOMMENDATION_LIMIT
}: {
  stories: StoryLearningIndexItem[]
  currentSlug?: string | null
  candidateLimit?: number
  limit?: number
}) {
  const { user } = useAuth()
  const storyLocators = useMemo(
    () => stories.map((story) => storyLearningLocator(story.slug)),
    [stories]
  )
  const { data: learningStates = new Map(), isLoading: progressLoading } =
    useLearningStates(storyLocators)

  const progressBySlug = useMemo(() => {
    const map = new Map<string, StoryProgressLike>()
    stories.forEach((story) => {
      const state = learningStates.get(
        learningContentKey(storyLearningLocator(story.slug))
      )
      if (!state) return
      map.set(story.slug, {
        status: state.status,
        progress: state.progress
      })
    })
    return map
  }, [learningStates, stories])

  const candidates = useMemo(
    () =>
      selectStoryRecommendationCandidates({
        stories,
        progressBySlug,
        currentSlug,
        limit: candidateLimit
      }),
    [candidateLimit, currentSlug, progressBySlug, stories]
  )
  const wordIds = useMemo(
    () => uniqueNumbers(candidates.flatMap((story) => story.wordIds)),
    [candidates]
  )
  const grammarIds = useMemo(
    () => uniqueStrings(candidates.flatMap((story) => story.grammarConceptIds)),
    [candidates]
  )

  return useQuery({
    queryKey: [
      'story-recommendations',
      user?.id,
      currentSlug,
      stableKey(candidates.map((story) => story.slug)),
      stableKey(wordIds),
      stableKey(grammarIds),
      stableKey(
        Array.from(progressBySlug.entries()).map(
          ([slug, progress]) =>
            `${slug}:${progress.status ?? 'new'}:${progress.progress ?? 0}`
        )
      )
    ],
    queryFn: async () => {
      const [wordStatuses, grammarStatuses] = await Promise.all([
        getWordFamiliarityStatuses(wordIds),
        getGrammarFamiliarityStatuses(grammarIds)
      ])

      return scoreStoryRecommendations({
        candidates,
        allStories: stories,
        progressBySlug,
        wordStatuses,
        grammarStatuses,
        currentSlug,
        limit
      })
    },
    enabled: Boolean(user?.id && candidates.length && !progressLoading),
    staleTime: 1000 * 60 * 5
  })
}
